import { W, weaponReady } from './weapons.js';
import { STAMINA_MAX, STAMINA_SPRINT_MIN } from './player.js';
import { remoteList } from './remote.js';
import { player, S, clock, R } from './state.js';
import { nearBox } from './mapgrid.js';

// ============================================================
//  HUD: патроны, здоровье, прицел; мини-карта (радар).
// ============================================================

const radar = document.getElementById('radar'), rctx = radar.getContext('2d');

export function updateHUD(moving) {
  document.getElementById('cur').textContent = W().ammo;
  if (S.reloading) {
    const p = 1 - Math.max(0, (S.reloadEnd - clock.elapsedTime) / W().reload);
    document.getElementById('rfill').style.width = (p * 100) + '%';
  }
  const hp = Math.round(player.hp);
  document.getElementById('hpNum').textContent = hp;
  const f = document.getElementById('hpFill');
  f.style.width = hp + '%'; f.classList.toggle('low', hp < 35);

  // Дыхание (стамина). Значение — из предсказанного состояния игрока, то есть
  // ровно то, по которому считается его скорость; сервер поправляет его кадром
  // OP_SELF (см. prediction.js). Три состояния читаются одним взглядом:
  // полное — синее, на исходе — жёлтое, пусто — красное и мигает, потому что
  // бег вернётся не сразу, а только после передышки (гистерезис в player.js).
  const st = Math.max(0, Math.min(1, player.stamina / STAMINA_MAX));
  const sf = document.getElementById('stFill');
  sf.style.width = (st * 100) + '%';
  sf.classList.toggle('low', st > 0 && st < 0.35);
  sf.classList.toggle('empty', st <= 0.001 || (!player.sprinting && st < STAMINA_SPRINT_MIN));

  // Пока оружие прижато к корпусу (бег), перекрестье гаснет и расходится.
  // Это не украшение: по нему в этот момент выстрела не будет ни у клиента,
  // ни на сервере, и молча врущий прицел читался бы как «не стреляет».
  const ready = weaponReady();
  document.getElementById('crosshair').style.opacity = ready ? '1' : '0.25';

  // прицел: расходится при движении и стрельбе, сжимается в ADS
  const spread = 6 + (moving ? 8 : 0) + (S.firing ? 6 : 0) + (S.aiming ? -4 : 0)
    + (ready ? 0 : 10);
  const L = 6, Wl = 2;
  const set = (cls, x, y, w, h) => {
    const el = document.querySelector('#crosshair .' + cls);
    el.style.width = w + 'px'; el.style.height = h + 'px';
    el.style.left = x + 'px'; el.style.top = y + 'px';
  };
  set('l-up', -Wl / 2, -spread - L, Wl, L); set('l-dn', -Wl / 2, spread, Wl, L);
  set('l-lf', -spread - L, -Wl / 2, L, Wl); set('l-rt', spread, -Wl / 2, L, Wl);
}

/**
 * Отладочная строка (клавиша F3): где стоит игрок и на чём именно.
 *
 * ⚠️ Зачем это в игре, а не в консоли. Жалоба «вот здесь пол мерцает» без
 * координат не проверяема: карта 140×140 м, мест похожих друг на друга — сотни,
 * а искать по описанию «около той стенки» — это часы. Сюда же выводится
 * МАТЕРИАЛ и ВЕРХ опорного бокса: у мерцания пола первый подозреваемый — две
 * поверхности на одной глубине, и увидеть их разницу в сантиметрах можно
 * только цифрами.
 *
 * `player.pos` — это ГЛАЗА (см. player.js::stepPhysics), поэтому ноги считаем
 * вычитанием роста: показывать игроку высоту глаз бессмысленно, ему нужна та
 * координата, по которой потом ищется бокс в map.json.
 */
export function debugLine() {
  const feetY = player.pos.y - player.eye;
  // Опора — самый высокий бокс под стопами, накрывающий точку по XZ.
  // Допуск 0.06 м: стоя на боксе, стопы ровно на его верхе, и строгое
  // сравнение выбросило бы как раз тот бокс, который ищем.
  let best = null;
  nearBox(player.pos.x - 0.1, player.pos.z - 0.1, player.pos.x + 0.1, player.pos.z + 0.1, (c) => {
    if (player.pos.x < c.min.x || player.pos.x > c.max.x) return;
    if (player.pos.z < c.min.z || player.pos.z > c.max.z) return;
    if (c.max.y > feetY + 0.06) return;
    if (!best || c.max.y > best.max.y) best = c;
  });
  const f = (v) => v.toFixed(2);
  return `X ${f(player.pos.x)} · Y ${f(feetY)} · Z ${f(player.pos.z)} — под ногами `
    + (best ? `${best.m}, верх ${f(best.max.y)}` : 'ничего');
}

export function drawRadar() {
  const s = 1.8; // px на юнит (арена 80 → 144px)
  rctx.clearRect(0, 0, 150, 150);
  const cx = 75 - player.pos.x * s, cy = 75 - player.pos.z * s; // север-вверх, игрок в центре
  rctx.strokeStyle = '#334155'; rctx.strokeRect(cx - R * s, cy - R * s, R * 2 * s, R * 2 * s);
  rctx.fillStyle = '#475569';
  // ⚠️ Рисуем только то, что попадает в окошко радара (150 px при 1.8 px/м —
  // это ±42 м вокруг игрока), а не всю карту. На блокауте из 57 коробок
  // разницы не было, на 1743 это 1743 вызова fillRect каждый кадр, из которых
  // подавляющее большинство рисовало за краем холста — работа впустую.
  const half = 75 / s + 1;
  nearBox(player.pos.x - half, player.pos.z - half, player.pos.x + half, player.pos.z + half, (c) => {
    if (c.w > 30 || c.d > 30) return;   // границы арены не рисуем
    rctx.fillRect(cx + (c.min.x) * s, cy + (c.min.z) * s, c.w * s, c.d * s);
  });
  // Всё живое приходит с сервера одним списком. Цвет — тот же язык, что
  // у моделей в remote.js: СОЮЗНИК зелёный (и бот, и человек — важно только
  // «можно ли в него стрелять»), чужой игрок синий, чужой бот серый.
  for (const r of remoteList().values()) {
    if (!r.group.visible) continue;
    rctx.fillStyle = r.ally ? '#34d399' : (r.npc ? '#9ca3af' : '#38bdf8'); rctx.beginPath();
    rctx.arc(cx + r.group.position.x * s, cy + r.group.position.z * s, 3.5, 0, 7); rctx.fill();
  }
  // игрок + направление взгляда
  rctx.save(); rctx.translate(75, 75); rctx.rotate(-S.yaw);
  rctx.fillStyle = '#4ade80'; rctx.beginPath();
  rctx.moveTo(0, -6); rctx.lineTo(4, 5); rctx.lineTo(-4, 5); rctx.closePath(); rctx.fill();
  rctx.restore();
}
